/**
 * ========================
 * PROJECTS DOMAIN MENU PROVIDER
 * ========================
 *
 * Menu configuration for the Projects domain (mounted at `/app/projects/*`).
 *
 * Role tiers (mirrors `tasksMenu` — OR logic via `anyVisibilityCheck`):
 *  - members  : Project.Read   OR Projects Access  → read-only surfaces
 *  - leaders  : Project.Create OR Project.Update    → create / edit own projects
 *  - admins   : Admin / ProjectAdmin role OR Project.Admin → full administration
 *
 * Sections:
 *  1. Overview        — read surfaces (Overview, Project List, Board, Calendar)
 *  2. Browse by Origin— per-origin filtered list views (Self / Department / Location / Organisation)
 *  3. My Work         — My Projects (members) + New Project (leaders)
 *  4. Insights        — Reports, Portfolios (admins)
 *  5. Administration  — Admin Dashboard, Lookups, Settings (admins)
 *
 * The per-origin "Browse" items are navigation shortcuts that pre-filter the
 * Project List via `?originId=<originID>` (PROJECTS_ROUTES.LIST_BY_ORIGIN, consumed by
 * `list/index.tsx`). They are
 * NOT the source of truth for origin values — every editable origin surface
 * (create wizard, edit form, list filter, admin lookups) is driven by the live
 * `useProjectOriginService` lookup. The seeded origin IDs (Self=1, Organisation=2,
 * Department=3, Location=4) are stable migration constants, used here only as deep-link targets.
 *
 * Paths mirror the routes wired in `pages/app/ProjectManagement/index.tsx` and the
 * `PROJECTS_ROUTES` constants in `@/config/routes`. Admin items are gated to the same
 * roles used by the `ProtectedRoute` guards, so menu visibility and route
 * protection stay in lockstep.
 */

import {
  IconLayoutDashboard,
  IconListDetails,
  IconLayoutBoard,
  IconCalendar,
  IconReportAnalytics,
  IconFolders,
  IconUserCircle,
  IconPlus,
  IconDatabase,
  IconSettings,
  IconBuilding,
  IconBuildingCommunity,
  IconMapPin,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection, DomainMenuConfig } from '../../layouts/menu/types';
import { anyVisibilityCheck } from './BaseMenuProvider';
import { PROJECTS_ROUTES } from '@/config/routes';

const PROJECTS_ADMIN_ROLES = ['Admin', 'ProjectAdmin'];

/** Seeded ProjectOrigin IDs (migrations 127/138). Used only as deep-link targets. */
const ORIGIN_ID = {
  self: 1,
  organisation: 2,
  department: 3,
  location: 4,
} as const;

class ProjectsMenuProvider implements DomainMenuProvider {
  domainId = 'projects';

  getMenuConfig(): DomainMenuConfig {
    // Member tier — anyone who can read projects.
    const members = anyVisibilityCheck(
      { roles: ['Projects Access'] },
      { permissions: ['Project.Read'] }
    );
    // Leader tier — can create and/or update projects.
    const leaders = anyVisibilityCheck(
      { permissions: ['Project.Create'] },
      { permissions: ['Project.Update'] }
    );
    // Admin tier — Admin/ProjectAdmin role OR explicit Project.Admin permission.
    const admins = anyVisibilityCheck(
      { roles: PROJECTS_ADMIN_ROLES },
      { permissions: ['Project.Admin'] }
    );

    const sections: MenuSection[] = [
      // ---------------- Overview (members) ----------------
      {
        id: 'projects-overview',
        label: 'Overview',
        visible: members,
        items: [
          { id: 'projects-dashboard', label: 'Overview',      path: '/app/projects',          icon: <IconLayoutDashboard size={16} />, visible: members },
          { id: 'projects-list',      label: 'Project List',  path: '/app/projects/list',     icon: <IconListDetails size={16} />,     visible: members },
          { id: 'projects-board',     label: 'Board',         path: '/app/projects/board',    icon: <IconLayoutBoard size={16} />,     visible: members },
          { id: 'projects-calendar',  label: 'Calendar',      path: '/app/projects/calendar', icon: <IconCalendar size={16} />,        visible: members },
        ],
      },

      // ---------------- Browse by Origin (members) ----------------
      // Deep-links into the Project List, pre-filtered by ?originId=<originID>.
      {
        id: 'projects-browse',
        label: 'Browse by Origin',
        visible: members,
        items: [
          { id: 'projects-origin-self',  label: 'My Projects',  path: PROJECTS_ROUTES.LIST_BY_ORIGIN(ORIGIN_ID.self),         icon: <IconUserCircle size={16} />,        visible: members },
          { id: 'projects-origin-dept',  label: 'Department',   path: PROJECTS_ROUTES.LIST_BY_ORIGIN(ORIGIN_ID.department),   icon: <IconBuilding size={16} />,          visible: members },
          { id: 'projects-origin-loc',   label: 'Location',     path: PROJECTS_ROUTES.LIST_BY_ORIGIN(ORIGIN_ID.location),     icon: <IconMapPin size={16} />,            visible: members },
          { id: 'projects-origin-org',   label: 'Organisation', path: PROJECTS_ROUTES.LIST_BY_ORIGIN(ORIGIN_ID.organisation), icon: <IconBuildingCommunity size={16} />, visible: members },
        ],
      },

      // ---------------- My Work (members + leaders) ----------------
      {
        id: 'projects-my-work',
        label: 'My Work',
        visible: members,
        items: [
          { id: 'projects-my-projects', label: 'My Projects', path: '/app/projects/my-projects', icon: <IconUserCircle size={16} />, visible: members },
          { id: 'projects-create',      label: 'New Project',  path: '/app/projects/create',      icon: <IconPlus size={16} />,       visible: leaders, badgeColor: 'blue' },
        ],
      },

      // ---------------- Insights (admins) ----------------
      {
        id: 'projects-insights',
        label: 'Insights',
        visible: admins,
        items: [
          { id: 'projects-reports',    label: 'Reports',    path: '/app/projects/reports',    icon: <IconReportAnalytics size={16} />, visible: admins },
          { id: 'projects-portfolios', label: 'Portfolios', path: '/app/projects/portfolios', icon: <IconFolders size={16} />,         visible: admins },
        ],
      },

      // ---------------- Administration (admins) ----------------
      {
        id: 'projects-admin',
        label: 'Administration',
        visible: admins,
        items: [
          { id: 'projects-admin-dashboard', label: 'Admin Dashboard', path: '/app/projects/admin/dashboard', icon: <IconLayoutDashboard size={16} />, visible: admins, requiredRoles: PROJECTS_ADMIN_ROLES },
          { id: 'projects-lookups',  label: 'Lookups',  path: '/app/projects/admin/lookups',  icon: <IconDatabase size={16} />, visible: admins, requiredRoles: PROJECTS_ADMIN_ROLES },
          { id: 'projects-settings', label: 'Settings', path: '/app/projects/admin/settings', icon: <IconSettings size={16} />, visible: admins, requiredRoles: PROJECTS_ADMIN_ROLES },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '3.0.0',
        lastUpdated: Date.now(),
        description: 'Project management — role-tiered overview, origin browse, my work, and admin',
      },
    };
  }
}

export const projectsMenuProvider = new ProjectsMenuProvider();
